import React, { useState, useEffect, useRef } from 'react';
import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns';
import { GanttVisualizationProfileControllerService, type GanttVisualizationProfileDto } from '../api';
import { useTranslation } from 'react-i18next';

interface GanttProfileSelectorProps {
  selectedProfileUuid?: string;
  onProfileChange: (profile: GanttVisualizationProfileDto | null) => void;
}

export const GanttProfileSelector: React.FC<GanttProfileSelectorProps> = ({
  selectedProfileUuid,
  onProfileChange
}) => {
  const { t } = useTranslation();
  const [profiles, setProfiles] = useState<GanttVisualizationProfileDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedValue, setSelectedValue] = useState<string | undefined>(undefined);
  const [isReady, setIsReady] = useState(false);
  const dropdownRef = useRef<DropDownListComponent>(null);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    loadProfiles();
    
    return () => {
      isMounted.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadProfiles is a local function; runs only on mount
  }, []);

  useEffect(() => {
    // Load default profile if no profile is selected
    if (!selectedProfileUuid && profiles.length > 0 && !selectedValue) {
      loadDefaultProfile();
    } else if (selectedProfileUuid && profiles.length > 0) {
      setSelectedValue(selectedProfileUuid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally excludes loadDefaultProfile (local fn) and selectedValue (would cause loop)
  }, [selectedProfileUuid, profiles]);

  const loadProfiles = async () => {
    try {
      setLoading(true);
      const data = await GanttVisualizationProfileControllerService.findAll();
      if (!isMounted.current) return;
      
      setProfiles(data || []);
      
      // If no profile is selected, try to find default
      if (!selectedProfileUuid && data && data.length > 0) {
        loadDefaultProfile();
      }
    } catch (error) {
      console.error('Failed to load profiles:', error);
      if (isMounted.current) {
        setProfiles([]);
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
        // Small delay to ensure DOM is ready before rendering dropdown
        setTimeout(() => {
          if (isMounted.current) {
            setIsReady(true);
          }
        }, 50);
      }
    }
  };

  const loadDefaultProfile = async () => {
    try {
      const defaultProfile = await GanttVisualizationProfileControllerService.getDefaultProfile();
      if (!isMounted.current) return;
      
      if (defaultProfile && defaultProfile.uuid) {
        setSelectedValue(defaultProfile.uuid);
        onProfileChange(defaultProfile);
      } else if (profiles.length > 0) {
        // Use first available profile
        const firstProfile = profiles[0];
        setSelectedValue(firstProfile.uuid || undefined);
        onProfileChange(firstProfile);
      }
    } catch (error) {
      console.error('Failed to load default profile:', error);
      if (!isMounted.current) return;
      
      // Fallback to first profile
      if (profiles.length > 0) {
        const firstProfile = profiles[0];
        setSelectedValue(firstProfile.uuid || undefined);
        onProfileChange(firstProfile);
      }
    }
  };

  const handleProfileChange = (args: any) => {
    if (!args || args.value === undefined) return;
    
    const selectedUuid = args.value;
    setSelectedValue(selectedUuid);
    const selectedProfile = profiles.find(p => p.uuid === selectedUuid);
    onProfileChange(selectedProfile || null);
  };

  // Don't render dropdown until data is ready
  if (loading || !isReady) {
    return (
      <div className="flex items-center gap-2">
        <label className="text-sm text-gray-600 whitespace-nowrap">
          {t('gantt.profile', 'Profil')}:
        </label>
        <div className="w-[250px] h-8 bg-gray-100 rounded animate-pulse flex items-center px-2">
          <span className="text-sm text-gray-400">{t('common.loading', 'Laden...')}</span>
        </div>
      </div>
    );
  }

  // Create options only when profiles are available
  const profileOptions = profiles.length > 0
    ? profiles.map(profile => ({
        text: `${profile.name}${profile.isDefault ? ' (Standard)' : ''}`,
        value: profile.uuid || ''
      }))
    : [];

  // The Syncfusion DropDownList throws when the value is not in the dataSource
  const validValue = selectedValue && profileOptions.some(opt => opt.value === selectedValue)
    ? selectedValue
    : undefined;

  // Don't render dropdown if no profiles available
  if (profileOptions.length === 0) {
    return (
      <div className="flex items-center gap-2">
        <label className="text-sm text-gray-600 whitespace-nowrap">
          {t('gantt.profile', 'Profil')}:
        </label>
        <span className="text-sm text-gray-400">{t('gantt.noProfiles', 'Keine Profile verfügbar')}</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <label className="text-sm text-gray-600 whitespace-nowrap">
        {t('gantt.profile', 'Profil')}:
      </label>
      <DropDownListComponent
        ref={dropdownRef}
        dataSource={profileOptions}
        fields={{ text: 'text', value: 'value' }}
        value={validValue}
        change={handleProfileChange}
        placeholder={t('gantt.selectProfile', 'Profil auswählen')}
        width="250px"
        cssClass="e-small"
      />
    </div>
  );
};


